import { useCallback, useMemo, useState } from "react";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { FieldRenderer } from "@/lib/dynamicForms/components/FieldRenderer.component";
import type { DynamicFormSchema } from "@/lib/dynamicForms/interfaces/dynamicForm.interface";
import type { FieldDefinition } from "@/services/form-definition/form-definition-core.types";
import {
	type GroupLayoutElement,
	type LayoutElement,
	LayoutTypes,
} from "../schemas/layout.schema";
import {
	buildFieldDefinitionsById,
	buildFieldDefinitionsByName,
	resolveFieldDefinitionByLayoutId,
} from "../utils/field-definition-map.util";
import { LayoutGroupAccordion } from "./layout-group-accordion";

/** Props for {@link LayoutRenderer}. */
export interface LayoutRendererProps {
	/** Root or subtree node to render (typically the parsed layout JSON root). */
	node: LayoutElement;
	schema: DynamicFormSchema;
	fieldDefinitions: FieldDefinition[];
	/**
	 * Index of this node among its parent's `elements` (for stable group ids when
	 * `Group.id` is absent).
	 */
	nodeIndex?: number;
	/** Coordinates expand/collapse across sibling collapsible Groups. */
	sectionExpansion?: SectionExpansionController;
}

export interface SectionExpansionController {
	isExpanded: (sectionId: string) => boolean;
	setExpanded: (sectionId: string, expanded: boolean) => void;
}

function layoutKey(node: LayoutElement, index: number): string {
	if (node.type === LayoutTypes.Field) {
		return `field:${node.fieldDefinitionId}:${index}`;
	}
	if (node.type === LayoutTypes.Group) {
		return `group:${node.label ?? "untitled"}:${index}`;
	}
	return `${node.type}:${index}`;
}

function sliceGroupElements(group: GroupLayoutElement): LayoutElement[] {
	if (group.maxItems == null) return group.elements;
	return group.elements.slice(0, Math.max(0, Math.floor(group.maxItems)));
}

function groupSectionId(group: GroupLayoutElement, index: number): string {
	return group.id ?? layoutKey(group, index);
}

/**
 * Collects renderable collapsible (`enabled`) Groups under a VerticalLayout.
 */
export function collectCollapsibleSectionMeta(
	elements: LayoutElement[],
): { id: string; defaultExpanded: boolean }[] {
	const meta: { id: string; defaultExpanded: boolean }[] = [];
	elements.forEach((el, i) => {
		if (el.type !== LayoutTypes.Group || !el.enabled) return;
		if (sliceGroupElements(el).length === 0) return;
		meta.push({
			id: groupSectionId(el, i),
			defaultExpanded: el.defaultExpanded,
		});
	});
	return meta;
}

function VerticalLayoutRenderer({
	schema,
	fieldDefinitions,
	elements,
}: {
	schema: DynamicFormSchema;
	fieldDefinitions: FieldDefinition[];
	elements: LayoutElement[];
}) {
	const { t } = useAppTranslation();
	const collapsibleMeta = useMemo(
		() => collectCollapsibleSectionMeta(elements),
		[elements],
	);
	const showExpandAll = collapsibleMeta.length > 1;

	const [expandedById, setExpandedById] = useState<Record<string, boolean>>(
		() =>
			Object.fromEntries(collapsibleMeta.map((m) => [m.id, m.defaultExpanded])),
	);

	const sectionExpansion = useMemo<SectionExpansionController | undefined>(
		() =>
			showExpandAll
				? {
						isExpanded: (sectionId) => expandedById[sectionId] ?? false,
						setExpanded: (sectionId, expanded) => {
							setExpandedById((prev) => ({
								...prev,
								[sectionId]: expanded,
							}));
						},
					}
				: undefined,
		[expandedById, showExpandAll],
	);

	const allExpanded =
		showExpandAll && collapsibleMeta.every((m) => expandedById[m.id] === true);

	const handleExpandAllChange = useCallback(
		(checked: boolean) => {
			setExpandedById(
				Object.fromEntries(collapsibleMeta.map((m) => [m.id, checked])),
			);
		},
		[collapsibleMeta],
	);

	return (
		<div className="flex flex-col gap-(--spacing-md,8px)">
			{showExpandAll ? (
				<Checkbox
					label={t("main.jobs.workspace.form.expandAllSections")}
					isSelected={allExpanded}
					onChange={handleExpandAllChange}
				/>
			) : null}
			{elements.map((el, i) => (
				<LayoutRenderer
					key={layoutKey(el, i)}
					schema={schema}
					fieldDefinitions={fieldDefinitions}
					node={el}
					nodeIndex={i}
					sectionExpansion={sectionExpansion}
				/>
			))}
		</div>
	);
}

/**
 * Recursively renders a validated {@link LayoutElement} tree (from {@link parseFormLayoutJson}).
 * Use inside the legacy dynamic form stack (`DynamicFormProvider` + `FieldRenderer`).
 *
 * ## Supported {@link LayoutTypes}
 *
 * ### `VerticalLayout` (`"VerticalLayout"`)
 * Stacks child nodes in a single column (`flex-col`) with `spacing-md` (8px) between
 * siblings (sections and fields). Use for the main form flow.
 * When two or more sibling Groups are collapsible (`enabled`), shows an
 * “Expand all sections” checkbox above the stack.
 *
 * **Shape:** `{ type: "VerticalLayout", elements: LayoutElement[] }`
 *
 * ### `HorizontalLayout` (`"HorizontalLayout"`)
 * Places child nodes in a row (`flex-row`, wraps on small widths). Each child is wrapped in a
 * flex item (`flex-1`, `min-w-0`) so siblings share horizontal space. Use for side‑by‑side
 * controls (e.g. name and date).
 *
 * **Shape:** `{ type: "HorizontalLayout", elements: LayoutElement[] }`
 *
 * ### `Group` (`"Group"`)
 * Groups nested layout nodes. When `enabled` is true, renders a collapsible
 * {@link LayoutGroupAccordion} (`AccordionItem`); `defaultExpanded` sets the initial
 * open state. When `enabled` is false, renders a static titled section with fields
 * stacked vertically (always visible).
 *
 * If `maxItems` is set (finite number ≥ 0), only the first `maxItems` entries in `elements`
 * are rendered; the rest are ignored (useful for capping visible fields in a section).
 *
 * **Shape:** `{ type: "Group", label?, enabled, defaultExpanded, maxItems?, elements }`
 *
 * ### `Field` (`"Field"`)
 * Leaf node: resolves `fieldDefinitionId` against context maps (OpenAPI `FieldDefinition.id`
 * first, then `name`), then renders {@link FieldRenderer}. Optional `label` overrides the
 * displayed label for that control. If no definition matches, an inline error is shown instead
 * of a control.
 *
 * **Shape:** `{ type: "Field", fieldDefinitionId: string, label? }`
 *
 * @param props - {@link LayoutRendererProps}
 * @returns React tree for this subtree, or an inline error for an unresolvable `Field`.
 */
export function LayoutRenderer({
	schema,
	fieldDefinitions,
	node,
	nodeIndex = 0,
	sectionExpansion,
}: LayoutRendererProps) {
	const fieldDefinitionsById = useMemo(
		() => buildFieldDefinitionsById(fieldDefinitions),
		[fieldDefinitions],
	);

	const fieldDefinitionsByName = useMemo(
		() => buildFieldDefinitionsByName(fieldDefinitions),
		[fieldDefinitions],
	);

	if (node.type === LayoutTypes.VerticalLayout) {
		return (
			<VerticalLayoutRenderer
				schema={schema}
				fieldDefinitions={fieldDefinitions}
				elements={node.elements}
			/>
		);
	}

	if (node.type === LayoutTypes.HorizontalLayout) {
		return (
			<div className="flex flex-row flex-wrap items-start gap-4">
				{node.elements.map((el, i) => (
					<div key={layoutKey(el, i)} className="min-w-0 flex-1">
						<LayoutRenderer
							schema={schema}
							fieldDefinitions={fieldDefinitions}
							node={el}
							nodeIndex={i}
							sectionExpansion={sectionExpansion}
						/>
					</div>
				))}
			</div>
		);
	}

	if (node.type === LayoutTypes.Group) {
		const groupElements = sliceGroupElements(node);
		// Empty sections (e.g. unused "New Section" groups) render nothing.
		if (groupElements.length === 0) {
			return null;
		}

		const children = groupElements.map((el, i) => (
			<LayoutRenderer
				key={layoutKey(el, i)}
				schema={schema}
				fieldDefinitions={fieldDefinitions}
				node={el}
				nodeIndex={i}
				sectionExpansion={sectionExpansion}
			/>
		));

		if (node.enabled) {
			const sectionId = groupSectionId(node, nodeIndex);
			const controlled = sectionExpansion != null;
			return (
				<LayoutGroupAccordion
					label={node.label}
					defaultExpanded={node.defaultExpanded}
					isExpanded={
						controlled ? sectionExpansion.isExpanded(sectionId) : undefined
					}
					onExpandedChange={
						controlled
							? (expanded) => sectionExpansion.setExpanded(sectionId, expanded)
							: undefined
					}
					progressNodes={groupElements}
					fieldDefinitionsById={fieldDefinitionsById}
					fieldDefinitionsByName={fieldDefinitionsByName}
				>
					<div className="flex flex-col gap-(--spacing-3xl,24px)">
						{children}
					</div>
				</LayoutGroupAccordion>
			);
		}

		return (
			<section className="flex w-full flex-col gap-(--spacing-lg,12px)">
				{node.label ? (
					<h2 className="text-md font-extrabold text-primary">{node.label}</h2>
				) : null}
				<div className="flex flex-col gap-(--spacing-3xl,24px)">{children}</div>
			</section>
		);
	}

	const fieldDefinition = resolveFieldDefinitionByLayoutId(
		node.fieldDefinitionId,
		fieldDefinitionsById,
		fieldDefinitionsByName,
	);
	if (!fieldDefinition) {
		return (
			<div role="alert" className="text-sm text-error-500">
				No field definition for id <strong>{node.fieldDefinitionId}</strong>.
			</div>
		);
	}

	return (
		<FieldRenderer
			fieldDefinition={fieldDefinition}
			layoutInfo={node.layoutInfo}
		/>
	);
}
